import {
  BigFilledButton,
  Div,
  LoadingIcon,
  RightArrowIcon,
  SmallCheckbox,
  Text3
} from "components/Base";
import { noop } from "utils/General";
import { actions, getters } from "Portal/CallForPapersForm/model";
import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router";
import Tooltip from "ui-kit/Tooltip";
import { addS } from "utils/General";
import {
  getLoginValuesValid,
  getNextButtonDisabled,
  getPortalUrl,
  getPrimaryButtonColor,
  getPrimaryButtonTextColor,
  getSectionHeaderColor,
  getSectionHeaderSize,
  getSubmissionLimit
} from "../selectors";
import BackButton from "./BackButton";
import Field from "./Field";

const decorate = connect(
  state => ({
    error: getters.error(state),
    emailError: getters.emailError(state),
    isLoggedIn: getters.isLoggedIn(state),
    searching: getters.searching(state),
    loginValues: getters.loginValues(state),
    shouldEnforceSso: getters.shouldEnforceSso(state),
    disableSignup: getters.disableSignup(state),
    customMessage: getters.customMessage(state),
    userExists: getters.userExists(state),
    isVirtualUser: getters.isVirtualUser(state),
    userValidated: getters.userValidated(state),
    loggedInLabel: getters.loggedInLabel(state),
    loggedInFirstName: getters.loggedInFirstName(state),
    loginValid: getLoginValuesValid(state),
    userFirstName: getters.userFirstName(state),
    userLastName: getters.userLastName(state),
    singleSignOnIdentityProviders: getters.singleSignOnIdentityProviders(state),
    formId: getters.formId(state),
    formData: getters.formData(state),
    buttonColor: getPrimaryButtonColor(state),
    buttonTextColor: getPrimaryButtonTextColor(state),
    sectionHeaderSize: getSectionHeaderSize(state),
    sectionHeaderColor: getSectionHeaderColor(state),
    nextButtonDisabled: getNextButtonDisabled(state),
    submissionLimit: getSubmissionLimit(state),
    portalUrl: getPortalUrl(state),
    totalEventSubmissionForUser: getters.totalEventSubmissionForUser(state),
    userCanSubmitNewSubmissions: getters.userCanSubmitNewSubmissions(state),
    termsAccepted: getters.termsAccepted(state)
  }),
  {
    updateLoginValues: actions.updateLoginValues,
    goToFormPage: actions.goToFormPage,
    validateLogin: actions.validateLogin,
    nextPage: actions.nextPage,
    handleLogout: actions.handleLogout,
    onLogin: actions.onLogin,
    onCreateAccount: actions.onCreateAccount,
    setIsPasswordValid: actions.setIsPasswordValid,
    setUserFirstName: actions.setUserFirstName,
    setUserLastName: actions.setUserLastName,
    fetchSingleSignOnIdentityProviders:
      actions.fetchSingleSignOnIdentityProviders,
    toggleTermsAccepted: actions.toggleTermsAccepted
  }
);

const LoginPage = ({
  handlers,
  isVirtualUser,
  isLoggedIn,
  loginValues,
  updateLoginValues,
  loginValid,
  shouldEnforceSso,
  disableSignup,
  customMessage,
  userExists,
  userValidated,
  validateLogin,
  searching,
  nextPage,
  error,
  loggedInFirstName,
  onLogin,
  onCreateAccount,
  setIsPasswordValid,
  userFirstName = null,
  userLastName = null,
  setUserFirstName = noop,
  setUserLastName = noop,
  singleSignOnIdentityProviders,
  formData,
  emailError = "",
  buttonColor,
  buttonTextColor,
  sectionHeaderColor,
  sectionHeaderSize,
  nextButtonDisabled,
  submissionLimit,
  portalUrl,
  totalEventSubmissionForUser,
  userCanSubmitNewSubmissions,
  termsAccepted,
  toggleTermsAccepted
}) => {
  const showAccountCreationFlow = isVirtualUser || !userExists;

  const ActionButton = (
    <BigFilledButton
      RightIcon={RightArrowIcon}
      style={{
        borderRadius: "20px",
        backgroundColor: buttonColor,
        color: buttonTextColor,
        borderColor: buttonColor
      }}
      onClick={nextPage}
      disabled={nextButtonDisabled}
      tooltio="test"
    >
      Continue to form
    </BigFilledButton>
  );

  return (
    <Div display="row">
      {isLoggedIn ? (
        <Div width={1}>
          <h1
            style={{ color: sectionHeaderColor, fontSize: sectionHeaderSize }}
            tabIndex={0}
          >
            {loggedInFirstName ? (
              <>Welcome, {loggedInFirstName}!</>
            ) : (
              <>You are logged in</>
            )}
          </h1>
          <Div width={1} bg="neutral0" bra={2} p={5}>
            <Div aria-role="main">
              {!submissionLimit || !totalEventSubmissionForUser ? (
                <>
                  Thank you for your interest in our event. You are now logged
                  in.
                </>
              ) : (
                <>
                  Thank you for your interest in our event. You have already
                  submitted {totalEventSubmissionForUser} time
                  {addS(totalEventSubmissionForUser)} to this event.{" "}
                  <Div mt={2}>
                    <Link
                      to={portalUrl}
                      style={{
                        textDecoration: "underline"
                      }}
                    >
                      View previous submissions
                    </Link>
                  </Div>
                </>
              )}
            </Div>
            <Div
              display="row.space-between"
              mt={6}
              aria-label="footer"
              tabIndex={0}
            >
              <BackButton />
              {!userCanSubmitNewSubmissions ? (
                <Tooltip tooltip="Submission limit reached">
                  {ActionButton}
                </Tooltip>
              ) : (
                ActionButton
              )}
            </Div>
          </Div>
        </Div>
      ) : (
        <Div width={1}>
          <h1
            style={{ color: sectionHeaderColor, fontSize: sectionHeaderSize }}
            tabIndex={0}
          >
            {userValidated
              ? !showAccountCreationFlow
                ? "Log in with your existing account"
                : "Create an account"
              : "Get started"}{" "}
          </h1>
          <Div width={1} bg="neutral0" bra={2} p={5} aria-role="main">
            {singleSignOnIdentityProviders.length === 0 &&
              !formData.event_enable_external_users && (
                <Div>
                  <Div>
                    Insufficient event authentication methods detected. Please
                    note that there are currently no active authentication
                    mechanisms in place for the event. Don't hesitate to reach
                    out your event team.
                  </Div>
                  <Div aria-label="footer" tabIndex={0}>
                    <BackButton />
                  </Div>
                </Div>
              )}
            <Div>
              <ul style={{ textAlign: "center" }}>
                {singleSignOnIdentityProviders.map(
                  singleSignOnIdentityProvider => {
                    return (
                      <li
                        style={{
                          textAlign: "center",
                          marginBottom: "10px"
                        }}
                        key={singleSignOnIdentityProvider.id}
                      >
                        <BigFilledButton
                          style={{
                            width: "100%",
                            backgroundColor: buttonColor,
                            color: buttonTextColor,
                            borderColor: buttonColor
                          }}
                          onClick={e => {
                            e.preventDefault();
                            localStorage.setItem(
                              "redirect_url",
                              window.location.href
                            );
                            window.location.href =
                              singleSignOnIdentityProvider.sso_url;
                          }}
                          tabIndex={0}
                        >
                          Log me in with{" "}
                          {singleSignOnIdentityProvider.vendor_app_name}
                        </BigFilledButton>
                      </li>
                    );
                  }
                )}
              </ul>
            </Div>
            {singleSignOnIdentityProviders.length > 0 &&
              formData.event_enable_external_users && (
                <Div style={{ textAlign: "center" }}>- or -</Div>
              )}

            {formData.event_enable_external_users && shouldEnforceSso ? (
              <Div>
                <Field
                  field={{
                    id: "email",
                    name: "Your Email Address:",
                    type: "email",
                    required: true,
                    settings: {},
                    isValid: true
                  }}
                  values={loginValues}
                  updateValues={updateLoginValues}
                  emailError={showAccountCreationFlow && emailError}
                />
                <Div bg="warning2" p={3} bra={1} mt={3} mb={3} tabIndex={0}>
                  Your email domain requires you to login or sign up using SSO.{" "}
                </Div>
              </Div>
            ) : formData.event_enable_external_users && disableSignup ? (
              <Div>
                <Field
                  field={{
                    id: "email",
                    name: "Your Email Address:",
                    type: "email",
                    required: true,
                    settings: {},
                    isValid: true
                  }}
                  values={loginValues}
                  updateValues={updateLoginValues}
                  emailError={showAccountCreationFlow && emailError}
                />
                <Div bg="warning2" p={3} bra={1} mt={3} mb={3} tabIndex={0}>
                  <span dangerouslySetInnerHTML={{ __html: customMessage }} />
                </Div>
              </Div>
            ) : formData.event_enable_external_users ? (
              <Div>
                <Field
                  field={{
                    id: "email",
                    name: "Your Email Address:",
                    type: "email",
                    required: true,
                    settings: {},
                    isValid: true
                  }}
                  values={loginValues}
                  updateValues={updateLoginValues}
                  emailError={showAccountCreationFlow && emailError}
                />
                {!userValidated && error && error.length ? (
                  <Div
                    fs={2}
                    color="danger8"
                    fw={3}
                    bra={1}
                    mt={1}
                    mb={1}
                    tabIndex={0}
                  >
                    {error}
                  </Div>
                ) : null}

                {userValidated ? (
                  <Field
                    tabIndex={0}
                    showPasswordHint={showAccountCreationFlow}
                    field={{
                      id: "password",
                      name: !showAccountCreationFlow
                        ? "Enter your existing password:"
                        : "Create a password:",
                      type: "password",
                      required: true,
                      settings: {},
                      isValid: true
                    }}
                    values={loginValues}
                    updateValues={updateLoginValues}
                    onPasswordChecklistChange={
                      !showAccountCreationFlow ? undefined : setIsPasswordValid
                    }
                  />
                ) : null}

                {showAccountCreationFlow && userValidated && (
                  <>
                    <Field
                      field={{
                        id: "firstName",
                        name: "First Name",
                        type: "text",
                        required: true,
                        settings: {},
                        isValid: true
                      }}
                      tabIndex={0}
                      values={userFirstName}
                      updateValues={e => setUserFirstName(e?.value?.value)}
                    />

                    <Field
                      field={{
                        id: "lastName",
                        name: "Last Name",
                        type: "text",
                        required: true,
                        settings: {},
                        isValid: true
                      }}
                      tabIndex={0}
                      values={userLastName}
                      updateValues={e => setUserLastName(e?.value?.value)}
                    />
                  </>
                )}

                {userValidated && error && error.length ? (
                  <Div
                    fs={2}
                    color="danger8"
                    fw={3}
                    bra={1}
                    mt={1}
                    mb={1}
                    tabIndex={0}
                  >
                    {error + " "}
                    <Link
                      onlyActiveOnIndex={true}
                      to="/forgot"
                      style={{ textDecoration: "underline" }}
                      target="_blank"
                      aria-label="Reset your password"
                    >
                      Reset your password
                    </Link>
                  </Div>
                ) : null}

                {showAccountCreationFlow && userValidated && (
                  <Div display="row.space-between.center">
                    <Div display="row.flex-start.center">
                      <SmallCheckbox
                        size={30}
                        selected={termsAccepted}
                        tabIndex={0}
                        onClick={toggleTermsAccepted}
                      />

                      <Text3 ml={4} color="black" bold>
                        I agree to the{" "}
                        <Link
                          to={{
                            pathname:
                              "https://www.sessionboard.com/legal/terms-of-service"
                          }}
                          target="_blank"
                          style={{ color: "#4263eb" }}
                        >
                          Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link
                          to={{
                            pathname:
                              "https://www.sessionboard.com/legal/privacy-policy"
                          }}
                          target="_blank"
                          style={{ color: "#4263eb" }}
                        >
                          Privacy Policy
                        </Link>{" "}
                      </Text3>
                      <Div color={"danger9"} fw={3} ml={1} fs={5}>
                        *
                      </Div>
                    </Div>
                  </Div>
                )}

                <Div aria-label="footer" tabIndex={0}>
                  {!userValidated ? (
                    <Div display="row.flex-end" mt={3}>
                      <BigFilledButton
                        style={{
                          borderRadius: "20px",
                          backgroundColor: buttonColor,
                          color: buttonTextColor,
                          borderColor: buttonColor
                        }}
                        onClick={validateLogin}
                        disabled={searching}
                        RightIcon={searching ? LoadingIcon : RightArrowIcon}
                      >
                        Next
                      </BigFilledButton>
                    </Div>
                  ) : null}

                  {userValidated ? (
                    <Div display="row.flex-end" mt={3}>
                      <BigFilledButton
                        RightIcon={searching ? LoadingIcon : RightArrowIcon}
                        style={{
                          borderRadius: "20px",
                          backgroundColor: buttonColor,
                          color: buttonTextColor,
                          borderColor: buttonColor
                        }}
                        onClick={() => {
                          handlers.setShowAlert(false);
                          if (!showAccountCreationFlow) {
                            onLogin();
                          } else {
                            onCreateAccount();
                          }
                        }}
                        disabled={!loginValid || searching}
                      >
                        {!showAccountCreationFlow ? "Log In" : "Create account"}
                      </BigFilledButton>
                    </Div>
                  ) : null}
                </Div>
              </Div>
            ) : null}
          </Div>
        </Div>
      )}
    </Div>
  );
};
export default decorate(LoginPage);
